import React, {
    useState, Fragment, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useStyle from './MenuItemStyle';
import UtilsService from '../../../services/UtilsService';
import { ViewMore } from './ViewMore';
import RegisteredIcon from '../../../assets/image/registered.png';

// Modified by AI on 05/26/2026. Edit #1.
const MenuItem = ({
    className,
    menuItem,
    onClick,
    subItem,
    isEmployerFilterApplied,
    toggleFilterApplying,
    menuLevel,
    testId,
    searchConfig = {},
    viewMoreConfig = {},
}) => {
    const {
        SearchComponent,
        value: searchValue,
        applySearchToMenuLevel,
        displaySearchToMenuLevel,
        EmptyComponent,
    } = searchConfig;

    const {
        maxQtyItems,
        viewMoreVisibilityLevel,
    } = viewMoreConfig;

    const { t } = useTranslation();

    const [isOpen, setOpenState] = useState(false);
    const [isShownAll, setIsShownAll] = useState(true);

    let labelLevel;

    if (subItem) {
        if (menuItem.subItems) {
            labelLevel = 2;
        } else {
            labelLevel = 3;
        }
    } else {
        labelLevel = 1;
    }

    const showSearchComponent = (displaySearchToMenuLevel === labelLevel) && !isEmpty(SearchComponent);
    const showEmptyComponent = (displaySearchToMenuLevel === labelLevel)
        && !isEmpty(EmptyComponent)
        && isEmpty(menuItem.subItems);

    useEffect(() => {
        const showViewMore = Boolean(maxQtyItems) && (labelLevel === viewMoreVisibilityLevel);
        const needCutOffExtraItems = showViewMore && (maxQtyItems < menuItem?.subItems?.length);
        const needShowAllItems = showViewMore && searchValue && (maxQtyItems >= menuItem?.subItems?.length);

        if (needCutOffExtraItems) {
            setIsShownAll(false);
            return;
        }

        if (needShowAllItems) {
            setIsShownAll(true);
        }
    }, [maxQtyItems, menuItem?.subItems, labelLevel, viewMoreVisibilityLevel, searchValue]);

    useEffect(() => {
        if (labelLevel === applySearchToMenuLevel) {
            const isNeedOpenMenu = !isEmpty(searchValue);
            setOpenState(isNeedOpenMenu);
        }
    }, [searchValue, applySearchToMenuLevel]);

    const showAll = useCallback(() => {
        setIsShownAll(true);
    }, []);

    const classes = useStyle({
        isOpen, menuLevel, labelLevel, isResellerPartner: menuItem?.isResellerPartner,
    });

    const rootClasses = UtilsService.getClassName([
        classes.root,
        className,
    ]);

    return (
        <Fragment>
            <div className={rootClasses} onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                setOpenState(!isOpen);
                menuItem.onClick();

                if (!menuItem.subItems) {
                    onClick();
                }
            }}>
                <div className={classes.labelWrapper}>
                    <span className={classes.label} data-test-id={menuItem.testId}>
                        {t(menuItem.label)}
                    </span>
                    {menuItem.isResellerPartner && (
                        <img src={RegisteredIcon} width="16px" height="16px" />
                    )}
                </div>
                { menuItem.subItems && subItem && <FontAwesomeIcon
                    icon={'chevron-left'}
                    className={classes.chevron}
                /> }
                { menuItem.hasActiveFilter && isOpen
                        && <span className={classes.actionButton}
                            onClick={toggleFilterApplying}
                            data-test-id={isEmployerFilterApplied ? 'employers-show-all' : 'employers-show-active'}
                        >
                            { isEmployerFilterApplied ? t('SHOW_ALL') : t('SHOW_ACTIVE_MENU') }
                        </span>
                }
            </div>
            { isOpen && menuItem.subItems && <div className={classes.subItems}>
                {
                    showSearchComponent && SearchComponent
                }
                {
                    showEmptyComponent && EmptyComponent
                }
                {
                    (isShownAll ? menuItem.subItems : menuItem.subItems?.slice(0, maxQtyItems) || []).map((item, index) => (
                        <MenuItem
                            subItem
                            key={index}
                            menuItem={item}
                            onClick={onClick}
                            menuLevel={menuLevel + 1}
                            maxQtyItems={maxQtyItems}
                            searchConfig={searchConfig}
                            viewMoreConfig={viewMoreConfig}
                        />
                    ))
                }
                {
                    !isShownAll && <ViewMore showAll={showAll}/>
                }
            </div> }
        </Fragment>
    );
};

MenuItem.propTypes = {
    className: PropTypes.string,
    menuItem: PropTypes.object,
    subItem: PropTypes.bool,
    menuLevel: PropTypes.number,
    testId: PropTypes.string,
};

export default MenuItem;
